import React from "react";
import { Box, Fade } from "@mui/material";
import { useAppSelector } from "../store/store";
import { selectIsAuthenticated } from "../store/slices/authSlice";

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
}

/**
 * ProtectedRoute Component
 *
 * Enterprise-grade route protection with smooth transitions and loading states.
 * Handles authentication checks and provides graceful fallback rendering.
 *
 * Features:
 * - Smooth fade transitions between authenticated and unauthenticated states
 * - Loading state during authentication check
 * - Optimized re-rendering with React.memo
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback,
}) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // If authenticated (user in localStorage), show protected content
  // HttpOnly cookie will be validated on first API call via baseQueryWithReauth
  if (isAuthenticated) {
    return (
      <Fade in={true} timeout={400}>
        <Box sx={{ minHeight: "100vh" }}>{children}</Box>
      </Fade>
    );
  }

  // If not authenticated, show the fallback (login screen) with smooth transition
  return (
    <Fade in={true} timeout={400}>
      <Box sx={{ minHeight: "100vh" }}>{fallback}</Box>
    </Fade>
  );
};

export default React.memo(ProtectedRoute);
